import { useLingui } from '@lingui/react/macro';
import { Pause, Play } from 'lucide-react';
import React from 'react';

import type { LatestMatch } from 'modules/main/domain/interfaces/latest-match';
import { useTickerScroll } from 'modules/main/ui/hooks';
import UDIconButton from 'ud-ui/components/icon-button';
import NewsTickerItem from './news-ticker-item';
import { Viewport } from './styles';

interface NewsTickerProps {
  matches: LatestMatch[];
}

/** Скорость автопрокрутки, px/с: заголовок читается на ходу, а за записью можно уследить, чтобы нажать. */
const PIXELS_PER_SECOND = 50;

/**
 * Бегущая строка последних матчей: бесконечная лента в одну линию, которую можно листать рукой.
 *
 * Автопрокрутка, пауза, петля и ручное управление — в `use-ticker-scroll.ts`. Здесь — разметка: список
 * выводится несколько раз встык, а доступна скринридеру и табуляции только одна копия (рабочая зона
 * петли): иначе каждая новость читалась бы дважды.
 *
 * Лента с движением получает видимую кнопку «Остановить / Запустить ленту» (WCAG 2.2.2): наведение и фокус
 * ленту только придерживают, а кнопка останавливает насовсем. Кнопка стоит **вне** корня с обработчиками
 * наведения и фокуса — иначе курсор на ней продолжал бы придерживать ленту, уже запущенную кнопкой.
 * Если весь список помещается в окно, ленты нет: одна копия без движения и без кнопки.
 *
 * Листать можно тачпадом и тачем (родной горизонтальный скролл) и перетаскиванием мыши; кнопок-стрелок нет.
 */
function NewsTicker({ matches }: NewsTickerProps) {
  const { t } = useLingui();
  const { viewportRef, listRef, copies, accessibleCopy, isAnimated, isPaused, togglePause, rootHandlers, viewportHandlers } =
    useTickerScroll({
      pixelsPerSecond: PIXELS_PER_SECOND,
      itemsKey: matches,
    });
  const toggleLabel = isPaused ? t`Запустить ленту` : t`Остановить ленту`;

  return (
    <div className="flex items-center gap-3">
      <div {...rootHandlers} className="min-w-0 flex-1" data-testid="news-ticker-root">
        <Viewport ref={viewportRef} className="min-w-0 flex-1" data-testid="news-ticker-viewport" {...viewportHandlers}>
          <div className="flex w-max">
            {Array.from({ length: copies }, (_, copyIndex) => {
              const isAccessible = copyIndex === accessibleCopy;

              return (
                <ul
                  key={copyIndex}
                  ref={isAccessible ? listRef : undefined}
                  className="flex shrink-0 items-center"
                  aria-hidden={isAccessible ? undefined : true}
                >
                  {matches.map((match) => (
                    <li key={match.id} className="shrink-0">
                      <NewsTickerItem match={match} isAccessible={isAccessible} />
                    </li>
                  ))}
                </ul>
              );
            })}
          </div>
        </Viewport>
      </div>
      {isAnimated && (
        <UDIconButton
          className="shrink-0"
          size="sm"
          icon={isPaused ? <Play size={16} /> : <Pause size={16} />}
          tooltip={toggleLabel}
          aria-label={toggleLabel}
          aria-pressed={isPaused}
          onClick={togglePause}
        />
      )}
    </div>
  );
}

export default NewsTicker;
